import React, { useMemo, useState } from 'react';
import { theme } from '../../styles/theme';
import { PASSIVE_TEMPLATES, PassiveBucket, PassiveTemplate } from '../../data/passives';

interface PassivesReferenceScreenProps {
  onBack: () => void;
}

const BUCKET_ORDER: PassiveBucket[] = [
  'training',
  'background',
  'contacts',
  'loadout',
  'perception',
  'economy',
  'combat',
];

const BUCKET_LABELS: Record<PassiveBucket, string> = {
  training: 'Training',
  background: 'Background',
  contacts: 'Contacts',
  loadout: 'Loadout',
  perception: 'Perception',
  economy: 'Economy',
  combat: 'Combat',
};

const BUCKET_DESCRIPTIONS: Record<PassiveBucket, string> = {
  training: 'Modifies core stats: AP, hand size, card costs, range, retention.',
  background: 'Modifies how signal, vision, and detection work for the operative.',
  contacts: "Adds resources, information, or items the player wouldn't otherwise have.",
  loadout: 'Alters what the operative starts each round with.',
  perception: 'Modifies what passive intel the player picks up from the agent.',
  economy: 'Modifies Marks, item values, or the round-end attrition flow.',
  combat: 'Modifies combat resolution, encounter rules, or weapon behavior.',
};

// Mirrors PassiveDraftScreen bucket colors
const BUCKET_COLORS: Record<PassiveBucket, string> = {
  training:    '#66ccff',
  background:  '#aa88ff',
  contacts:    '#ffaa44',
  loadout:     '#44cc88',
  perception:  '#ff5ac4',
  economy:     '#ffcc44',
  combat:      '#ff5555',
};

export function PassivesReferenceScreen({ onBack }: PassivesReferenceScreenProps) {
  const [filter, setFilter] = useState<PassiveBucket | 'all'>('all');

  const grouped = useMemo(() => {
    const byBucket: Record<PassiveBucket, PassiveTemplate[]> = {
      training: [], background: [], contacts: [], loadout: [],
      perception: [], economy: [], combat: [],
    };
    for (const template of Object.values(PASSIVE_TEMPLATES)) {
      byBucket[template.bucket].push(template);
    }
    for (const b of BUCKET_ORDER) {
      byBucket[b].sort((a, b2) => a.name.localeCompare(b2.name));
    }
    return byBucket;
  }, []);

  const visibleBuckets = filter === 'all' ? BUCKET_ORDER : [filter];
  const totalCount = Object.values(PASSIVE_TEMPLATES).length;

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.headerRow}>
          <button style={styles.backButton} onClick={onBack}>← MENU</button>
          <h1 style={styles.title}>PASSIVES</h1>
          <div style={styles.headerCount}>{totalCount} TOTAL</div>
        </div>

        <div style={styles.filterRow}>
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            ALL
          </FilterButton>
          {BUCKET_ORDER.map(b => (
            <FilterButton
              key={b}
              active={filter === b}
              color={BUCKET_COLORS[b]}
              onClick={() => setFilter(b)}
            >
              {BUCKET_LABELS[b].toUpperCase()}
            </FilterButton>
          ))}
        </div>

        {visibleBuckets.map(bucket => {
          const passives = grouped[bucket];
          if (passives.length === 0) return null;
          const color = BUCKET_COLORS[bucket];
          return (
            <section key={bucket} style={styles.section}>
              <div style={{ ...styles.sectionHeader, color }}>
                <span style={styles.sectionMark}>▍</span>
                {BUCKET_LABELS[bucket].toUpperCase()} · {passives.length}
              </div>
              <div style={styles.bucketDescription}>{BUCKET_DESCRIPTIONS[bucket]}</div>
              <div style={styles.grid}>
                {passives.map(passive => (
                  <PassiveEntry key={passive.key} passive={passive} color={color} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

// --- Filter button ---

interface FilterButtonProps {
  active: boolean;
  color?: string;
  onClick: () => void;
  children: React.ReactNode;
}

function FilterButton({ active, color = theme.colors.textSecondary, onClick, children }: FilterButtonProps) {
  return (
    <button
      style={{
        ...styles.filterButton,
        borderColor: active ? color : theme.colors.border,
        color: active ? color : theme.colors.textSecondary,
        background: active ? `${color}14` : 'transparent',
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// --- Passive entry ---

interface PassiveEntryProps {
  passive: PassiveTemplate;
  color: string;
}

function PassiveEntry({ passive, color }: PassiveEntryProps) {
  return (
    <div style={{ ...styles.passiveEntry, borderColor: `${color}40` }}>
      <div style={styles.passiveHeader}>
        <div style={{ ...styles.passiveName, color }}>{passive.name}</div>
        {passive.starterFriendly && (
          <span style={styles.starterBadge}>STARTER</span>
        )}
      </div>
      <div style={styles.passiveDesc}>{passive.description}</div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    overflow: 'auto',
    fontFamily: theme.fonts.mono,
  },
  inner: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px 32px 64px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    padding: '8px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '6px',
    color: theme.colors.textPrimary,
    margin: 0,
    flex: 1,
  },
  headerCount: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    fontWeight: 600,
  },
  filterRow: {
    display: 'flex',
    gap: '6px',
    marginBottom: '32px',
    flexWrap: 'wrap',
  },
  filterButton: {
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '2px',
    padding: '6px 12px',
    border: '1px solid',
    borderRadius: '3px',
    cursor: 'pointer',
    transition: 'all 0.12s',
  },
  section: {
    marginBottom: '36px',
  },
  sectionHeader: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '3px',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  sectionMark: {
    fontSize: '16px',
    lineHeight: 1,
  },
  bucketDescription: {
    fontSize: '10px',
    color: theme.colors.textDim,
    lineHeight: 1.5,
    marginBottom: '14px',
    paddingLeft: '4px',
    letterSpacing: '0.3px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '10px',
  },
  passiveEntry: {
    padding: '12px 14px',
    background: 'rgba(12, 16, 24, 0.6)',
    border: '1px solid',
    borderRadius: '4px',
  },
  passiveHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '6px',
  },
  passiveName: {
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  starterBadge: {
    fontSize: '7px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    padding: '2px 6px',
    borderRadius: '2px',
    background: 'rgba(68, 204, 136, 0.15)',
    border: '1px solid rgba(68, 204, 136, 0.4)',
    color: '#44cc88',
  },
  passiveDesc: {
    fontSize: '11px',
    lineHeight: 1.55,
    color: theme.colors.textSecondary,
  },
};
